import KControl from '../../core/mixins/control/KControl'
import KPopover from '../popover'
import SelectInput from './SelectInput'
import SelectDropdown from './SelectDropdown'
import { convertPropClass2ClassObjectLike } from '../../core/utils/KProps'
import { KeyCode } from '../../core/KConstants'
import _ from 'lodash'

export default {
  name: 'KSelect',
  mixins: [KControl],
  props: {
    popperClass: {
      // 自定义 popper 类，将作用于 popper 上面
      validator: (value) => _.isString(value) || _.isArray(value) || _.isObjectLike(value),
      default() {
        return {}
      },
    },
    popperMaxHeight: {
      type: String,
      default: null,
    },
    placement: {
      type: String,
      default: 'bottom-start',
    },
    value: {
      // validator: (value) => _.isString(value) || _.isArray(value),
      validator: () => true,
      default: null,
    },
    placeholder: {
      type: String,
      default: '请选择',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    size: {
      validator: (value) => ['large', 'normal', 'small', 'mini'].indexOf(value) !== -1,
      default: 'normal',
    },
    optionWidthAuto: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    supportSelectAll: { // 支持全选/全不选，仅在 multiple 为 true 的时候有效
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    searchPlaceholder: {
      type: String,
      default: '请输入关键字',
    },
    searchIcon: {
      type: String,
      default: 'search',
    },
    blockPropagation: {
      type: Boolean,
    },
    showClose: {
      type: Boolean,
      default: false,
    },
    pure: {
      type: Boolean,
      default: false,
    },
    adjustOnChange: {
      type: Boolean,
      default: false,
    },
    // 隐藏开关指示器
    hideIndicator: {
      type: Boolean,
      default: false,
    },
    inputable: {
      type: Boolean,
      default: false,
    },
    // sync
    inputValue: {
      type: String,
      default: '',
    },
    hideWhenNoOptions: {
      type: Boolean,
      default: false,
    },
    prefix: {
      // 前缀图标
      type: String,
      default: '',
    },
    // suffix: {
    //   // 后缀图标
    //   type: String,
    //   default: '',
    // },
    // inputClass: {
    //   validator: value => _.isString(value) || _.isArray(value) || _.isObjectLike(value),
    //   default() {
    //     return {}
    //   },
    // },
    // 多选时 最多选择几项
    multipleLimit: {
      type: Number,
      default: Infinity,
    },
    // 点击选项之前触发函数
    beforeClickOption: {
      type: Function,
      default: undefined,
    },
    keepHovered: {
      type: Boolean,
      default: false,
    },
    arrow: {
      type: Boolean,
      default: false,
    },
    beforeShow: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      active: false,
      innerValue: '',
      selectedOption: null,
      optionCount: 0,
    }
  },
  computed: {
    popperClassObjectLike() {
      return _.assign(
        {
          'km-select__dropdown-wrapper': true,
        },
        convertPropClass2ClassObjectLike(this.popperClass),
      )
    },
  },
  watch: {
    optionCount(value) {
      if (this.hideWhenNoOptions) {
        this.active = value !== 0
      }
    },
    active(value) {
      this.$emit(value ? 'open' : 'close')
    },
    value: {
      immediate: true,
      handler(value) {
        this.innerValue = value
      },
    },
    innerValue(value) {
      this.$emit('input', value)
      // 处理空值情况：
      if (this.multiple) {
        if (!value.length && this.selectedOption && this.selectedOption.length) {
          this.selectedOption = []
        }
      } else if (value === '' || _.isNil(value)) {
        this.selectedOption = null
      }
    },
    selectedOption() {
      if (this.multiple) {
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.$refs.popover && this.$refs.popover.updatePosition()
          })
        })
      }
    },
  },
  created() {
    if (this.multiple) {
      this.selectedOption = []
    }
  },
  mounted() {
  },
  methods: {
    handleHideWhenNoOptions() {
      if (this.hideWhenNoOptions && this.optionCount === 0) {
        return false
      }
      return true
    },
    handleBeforeShow() {
      if (this.beforeShow) {
        return this.beforeShow() && this.handleHideWhenNoOptions()
      }
      return this.handleHideWhenNoOptions()
    },
    renderInput() {
      const events = {
        'action:keydown': this.handleKeyboard,
        'action:clear': this.handleClear,
        'action:labelClick': this.handleOptionClick,
        'action:inputFocus': this.handleInputFocus,
        'action:inputBlur': this.handleInputBlur,
        'inputToken': (value) => {
          this.$emit('update:inputValue', value)
        },
      }

      return (
        <SelectInput
          ref="selectInput"
          placeholder={this.placeholder}
          active={this.active}
          clearable={this.clearable}
          disabled={this.disabled}
          loading={this.loading}
          size={this.size}
          multiple={this.multiple}
          selectedOption={this.selectedOption}
          pure={this.pure}
          hideIndicator={this.hideIndicator}
          inputable={this.inputable}
          inputValue={this.inputValue}
          prefix={this.prefix}
          {...{ on: events }}
        >
          {
            this.$scopedSlots.label
              ? this.$scopedSlots.label(this.multiple ? this.selectedOption || [] : {
                label: this.selectedOption ? this.selectedOption.label : '',
                value: this.selectedOption ? this.selectedOption.value : '',
              })
              : null
          }
        </SelectInput>
      )
    },
    renderOptionDropdown() {
      const events = {
        'option-click': this.handleOptionClick,
        'option-select': this.handleOptionSelect,
        'select-all': this.handleSelectAll,
        'unselect-all': this.handleUnselectAll,
        'search-update': this.handleSearch,
        'search-keydown': this.handleKeyboard,
        'load-more': this.handleLoadMore,
        'option-update': (options) => {
          this.optionCount = options.length
        },
      }

      return (
        <SelectDropdown
          ref="dropdown"
          multiple={this.multiple}
          supportSelectAll={this.supportSelectAll}
          selectedValue={this.innerValue}
          visible={this.active}
          showSearch={this.showSearch}
          autoFocus={this.autoFocus}
          searchPlaceholder={this.searchPlaceholder}
          searchIcon={this.searchIcon}
          loading={this.loading}
          showClose={this.showClose}
          keepHovered={this.keepHovered}
          {...{ on: events }}
        >
          {this.$slots.default}
          <template slot="searchExtra">
            {this.$slots.searchExtra}
          </template>
          <template slot="searchFooter">
            {this.$slots.searchFooter}
          </template>
          <template slot="loadMore">
            {this.$slots.loadMore}
          </template>
          <template slot="header">
            {this.$slots.header}
          </template>
          <template slot="footer">
            {this.$slots.footer}
          </template>
        </SelectDropdown>
      )
    },
    handleSearch(token) {
      this.$emit('search', token)
    },
    handleInputFocus() {
      this.$emit('inputFocus')
    },
    handleInputBlur() {
      this.$emit('inputBlur')
    },
    handleSelectAll() {
      if (!this.multiple) {
        return
      }

      this.innerValue = _.map(this.$refs.dropdown.options, option => option.value)
    },
    handleUnselectAll() {
      if (!this.multiple) {
        return
      }
      this.innerValue = []
    },
    handleOptionSelect(option) {
      const selected = option.selected
      if (!this.multiple && selected) {
        this.selectedOption = option
        return
      }

      if (this.multiple) {
        this.selectedOption = this.selectedOption || []
        // const index = this.selectedOption.indexOf(option)
        const selectedValues = _.map(this.selectedOption, option => option.value)
        const index = selectedValues.indexOf(option.value)

        if (selected) {
          if (index === -1) {
            this.selectedOption.push(option)
          }
        } else if (index !== -1) {
          this.selectedOption.splice(index, 1)
        }
      }
    },
    handleOptionClick(option) {
      if (this.beforeClickOption && typeof this.beforeClickOption === 'function') {
        this.beforeClickOption(() => {
          this.handleOptionClickAfter(option)
        }, _.clone(option))
      } else {
        this.handleOptionClickAfter(option)
      }
    },
    handleOptionClickAfter(option) {
      const optionValue = option.value

      if (!this.multiple) {
        this.active = false
        this.innerValue = optionValue
      } else {
        const index = this.innerValue.indexOf(optionValue)
        if (index !== -1) {
          this.innerValue.splice(index, 1)
        } else if (this.multipleLimit && this.innerValue.length < this.multipleLimit) {
          this.innerValue.push(optionValue)
        }
      }

      this.$nextTick(() => {
        this.$refs.selectInput.focus()
      })
    },
    handleLoadMore() {
      if (!this.loading) {
        this.$emit('loadMore')
      }
    },
    handleKeyboard(keyCode) {
      if (!this.active) {
        if (keyCode === KeyCode.UP || keyCode === KeyCode.DOWN || keyCode === KeyCode.ENTER) {
          this.active = true
        }
        return
      }

      const dropdown = this.$refs.dropdown
      if (this.active) {
        if (keyCode === KeyCode.ESC) {
          this.active = false
        } else if (keyCode === KeyCode.UP || keyCode === KeyCode.DOWN) {
          keyCode === KeyCode.UP ? dropdown.hoverPrevOption() : dropdown.hoverNextOption()
        } else if (keyCode === KeyCode.ENTER) {
          dropdown.selectOption()
        }
      }
    },
    handleClear() {
      if (this.multiple) {
        this.innerValue = []
      } else {
        this.innerValue = null
      }
      this.$emit('clear')
      this.handleResetDropdown()
    },
    handleResetDropdown() {
      this.$refs.dropdown.reset()
    },
    // ===== API: =====
    open() {
      setTimeout(() => {
        this.active = true
      }, 0)
    },
    close() {
      this.active = false
    },
  },
  render() {
    const input = this.renderInput()
    const optionDropdown = this.renderOptionDropdown()

    const events = {}

    return (
      <KPopover
        ref="popover"
        vModel={this.active}
        popperClass={this.popperClassObjectLike}
        widthEqualToReference={!this.optionWidthAuto}
        placement={this.placement || 'bottom-start'}
        blockPropagation={this.blockPropagation}
        adjustOnChange={this.adjustOnChange}
        beforeShow={this.handleBeforeShow}
        arrow={this.arrow}
        style={{
          maxHeight: this.popperMaxHeight,
        }}
        {...{ on: events }}
        lazyRender={false}
      >
        {optionDropdown}
        <template slot="reference">
          {input}
        </template>
      </KPopover>
    )
  },
}
